import { toHalfWidthAscii } from './toHalfWidthAscii';

import { BrokerageContractKind } from '@release/connect-gen/legacy/releasepf/anken_pb';
import { getDay } from 'date-fns/getDay';
import { getHours } from 'date-fns/getHours';
import { getMinutes } from 'date-fns/getMinutes';
import { isSameDay } from 'date-fns/isSameDay';
import { isThisWeek } from 'date-fns/isThisWeek';
import { isThisYear } from 'date-fns/isThisYear';
import { isToday } from 'date-fns/isToday';
import { isYesterday } from 'date-fns/isYesterday';
import { lightFormat } from 'date-fns/lightFormat';
import { parsePhoneNumberFromString } from 'libphonenumber-js/min';

/**
 * 法人の場合は「法人名（個人名）」、個人の場合は個人名のみを返す。
 * 法人名が空なら個人名のみ、個人名が空なら法人名のみを返す（両方空なら空文字）。
 */
export const formatNameWithCompany = (personName: string, isCompany?: boolean, companyName?: string): string => {
  const trimmedPersonName = personName.trim();
  if (!isCompany) return trimmedPersonName;
  const trimmedCompanyName = (companyName ?? '').trim();
  if (!trimmedCompanyName) return trimmedPersonName;
  return trimmedPersonName ? `${trimmedCompanyName}（${trimmedPersonName}）` : trimmedCompanyName;
};

export const formatDate = (date: Date | string): string => {
  if (!date) {
    return '';
  }
  const tmp = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(tmp.getTime())) {
    return '';
  }
  try {
    return lightFormat(tmp, 'yyyy/MM/dd');
  } catch (e) {
    console.error('Invalid date format:', e);
    return '';
  }
};

// 曜日を取得するヘルパー関数
const getWeekdayInJapanese = (date: Date): string => {
  const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
  return weekdays[date.getDay()];
};

// カスタムフォーマットで日付を整形する関数
export const formatDateCustom = (date: Date | string, formatPattern: string): string => {
  if (!date) {
    return '';
  }
  const tmp = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(tmp.getTime())) {
    return '';
  }
  try {
    // 特別な置換処理：{weekday}を曜日に置換
    if (formatPattern.includes('{weekday}')) {
      const weekday = getWeekdayInJapanese(tmp);
      // {weekday}を実際の曜日に置換してからdate-fnsでフォーマット
      const patternWithWeekday = formatPattern.replace('{weekday}', weekday);
      return lightFormat(tmp, patternWithWeekday);
    }
    return lightFormat(tmp, formatPattern);
  } catch (e) {
    console.error('Invalid date format:', e);
    return '';
  }
};

// 曜日付きの日付フォーマット（よく使われるパターン）
export const formatDateWithWeekday = (date: Date | string): string => {
  return formatDateCustom(date, 'yyyy/MM/dd {weekday}');
};

export const formatDateWithWeekdayParentheses = (date: Date | string): string => {
  return formatDateCustom(date, 'yyyy/MM/dd（{weekday}）');
};

export const formatDateTime = (date: Date | string): string => {
  if (!date) {
    return '';
  }
  const tmp = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(tmp.getTime())) {
    return '';
  }
  try {
    return lightFormat(tmp, 'yyyy/MM/dd HH:mm');
  } catch (e) {
    console.error('Invalid date format:', e);
    return '';
  }
};

// YYYY/mm/dd 00:00の場合は、日付を返す
// 時刻が明示されている場合はそのまま返す
export const formatDueDate = (date: Date | string): string => {
  const tmp = typeof date === 'string' ? new Date(date) : date;
  if (getHours(tmp) === 0 && getMinutes(tmp) === 0) {
    return formatDate(tmp);
  }
  return formatDateTime(tmp);
};

export const formatDateKanji = (date: Date | string): string => {
  const tmp = typeof date === 'string' ? new Date(date) : date;
  return lightFormat(tmp, 'yyyy年M月d日');
};

export const formatMonthKanji = (date: Date | string): string => {
  const tmp = typeof date === 'string' ? new Date(date) : date;
  return lightFormat(tmp, 'yyyy年MM月');
};

export const formatMonthDateKanji = (date: Date | string): string => {
  const tmp = typeof date === 'string' ? new Date(date) : date;
  return lightFormat(tmp, 'M月d日');
};
export const formatTime = (date: Date | string): string => {
  const tmp = typeof date === 'string' ? new Date(date) : date;
  return lightFormat(tmp, 'HH:mm');
};

export const formatDisplayRelativeTime = (date: Date | string): string => {
  const tmp = typeof date === 'string' ? new Date(date) : date;
  if (isToday(tmp)) {
    return formatTime(tmp);
  }
  if (isYesterday(tmp)) {
    return '昨日';
  } else if (isThisWeek(tmp)) {
    const d = getDay(tmp);
    switch (d) {
      case 0:
        return '日曜日';
      case 1:
        return '月曜日';
      case 2:
        return '火曜日';
      case 3:
        return '水曜日';
      case 4:
        return '木曜日';
      case 5:
        return '金曜日';
      case 6:
        return '土曜日';
    }
  } else if (isThisYear(tmp)) {
    return lightFormat(tmp, 'M/d');
  }
  return lightFormat(tmp, 'yyyy/M/d');
};

// 当日の場合は時刻のみ、それ以外は日付を返す
export const formatDisplayDateTime = (date: Date | string): string => {
  const tmp = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  if (isSameDay(tmp, now)) {
    return formatTime(tmp);
  }
  return lightFormat(tmp, 'yyyy/M/d');
};

// 電話番号を国ごとの形式にフォーマットする
export const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) {
    return '';
  }
  try {
    const parsed = parsePhoneNumberFromString(phoneNumber, 'JP');
    if (!parsed || !parsed.isValid()) {
      return phoneNumber;
    }
    return parsed.formatNational();
  } catch (e) {
    // パースに失敗した場合は、元の数値をそのまま返す
    return phoneNumber;
  }
};

// 電話番号をハイフンなしの形式にフォーマットする
export const formatPhoneNumberWithoutHyphen = (phoneNumber: string): string => {
  const phoneNumberWithPhyphen = formatPhoneNumber(phoneNumber);
  return phoneNumberWithPhyphen.replace(/-/g, '');
};

export const formatJPPostCode = (postCode: string): string => {
  return postCode.replace(/(\d{3})(\d{4})/, '$1-$2');
};

// 郵便番号を保持用の表記（ハイフンなしの数字のみ）に正規化する。formatJPPostCode の逆向き。
// 保持形式が揺れると郵便番号どうしの一致判定（読取値のままか手入力で上書きされたかの判別）が壊れるため、
// 入力・永続化データの復元の両方でこれを通す。
// 全角数字（１０５００７３ 等）は \D に一致して落ちてしまうため、先に半角化してから数字だけを残す。
export const normalizePostalCode = (postCode: string): string => {
  return toHalfWidthAscii(postCode).replace(/\D/g, '');
};

const priceFormatter = new Intl.NumberFormat('ja-JP');

// 文字列を3桁ごとにカンマ区切りにする
export const formatPriceWithComma = (price: string | number): string => {
  let numPrice: number;
  if (typeof price === 'string') {
    numPrice = Number.parseInt(price, 10);
  } else {
    numPrice = price;
  }
  if (Number.isNaN(numPrice)) {
    return '';
  }

  return priceFormatter.format(numPrice);
};

// 価格を3桁ごとにカンマ区切りにし、単位を "ten thousand yen" 表示にする
export const formatPriceInTenThousandYen = (price: string | number): string => {
  let numPrice: number;

  // 文字列か数値を判定し、数値に変換
  if (typeof price === 'string') {
    numPrice = Number.parseInt(price, 10);
  } else {
    numPrice = price;
  }

  // 数値が無効なら空文字を返す
  if (Number.isNaN(numPrice)) {
    return '';
  }

  const priceInTenThousands = numPrice / 10000;

  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return `${formatter.format(priceInTenThousands)}万円`;
};

export const formatFileSize = (size: number): string => {
  if (size < 1024) {
    return `${size}B`;
  }
  if (size < 1024 * 1024) {
    return `${Math.floor((10 * size) / 1024) / 10}KB`;
  }
  return `${Math.floor((10 * size) / 1024 / 1024) / 10}MB`;
};

export const formatJapaneseEra = (year: number): string => {
  if (2019 < year) {
    return `令和${year - 2018}年`;
  } else if (year === 2019) {
    return '平成31年/令和元年';
  } else if (1989 < year && year < 2019) {
    return `平成${year - 1988}年`;
  } else if (year === 1989) {
    return '昭和64年/平成元年';
  } else if (1926 < year && year < 1989) {
    return `昭和${year - 1925}年`;
  } else if (year === 1926) {
    return '大正15年/昭和元年';
  } else {
    return `明治以前`;
  }
};

export const formatBrokerageContractKind = (ankenKind: BrokerageContractKind): string => {
  switch (ankenKind) {
    case BrokerageContractKind.NONE:
      return '';
    case BrokerageContractKind.NON_EXCLUSIVE:
      return '一般';
    case BrokerageContractKind.EXCLUSIVE:
      return '専任';
    case BrokerageContractKind.EXCLUSIVE_RIGHT:
      return '専属専任';
  }
};

export const formatToSnakeCase = (str: string): string => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

// 年齢計算関数（誕生日を考慮した正確な計算）
export const computeAge = (dob: Date): number => {
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }

  return age;
};
